'use client'

import { useCallback, useEffect, useRef } from 'react'
import { useAPI } from '@aw/lib'
import { ServiceModules } from '@/lib/constants/services'
import { useAppDispatch, useAppSelector } from './store'
import {
    setToken,
    clearToken,
} from '../../store/slices/authToken'

const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000

const TOKEN_BROADCAST_CHANNEL = 'auth-token-sync'

interface BroadcastMessage {
    type: 'TOKEN_UPDATE' | 'TOKEN_CLEAR'
    token?: string
    expiresIn?: number
}

interface AuthTokenResponse {
    access_token: string
    token_type: string
    expires_in: number
}

interface UseAuthTokenReturn {
    token: string | null
    isLoading: boolean
    isError: boolean
    refreshToken: () => Promise<void>
    clearAuthToken: () => void
}

function getDeviceInfo() {
    if (typeof window === 'undefined') return {}
    return {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    }
}

export function useAuthToken(): UseAuthTokenReturn {
    const dispatch = useAppDispatch()
    const { currentOrganization } = useAppSelector((state) => state.workspace)
    const { token, expiresAt } = useAppSelector((state) => state.authToken)

    const api = useAPI()

    const channelRef = useRef<BroadcastChannel | null>(null)
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const isRefreshingRef = useRef(false)
    const hasInitialTokenRef = useRef(false)
    const isLoadingRef = useRef(false)
    const isErrorRef = useRef(false)

    // Store mutable values in refs to avoid useCallback/useEffect dependency cycles
    const apiRef = useRef(api)
    const orgRef = useRef(currentOrganization)
    const dispatchRef = useRef(dispatch)
    const tokenRef = useRef(token)
    // Raw timestamp (not a derived boolean) so the visibility handler can
    // evaluate expiry against the wall clock at handler-call time. A derived
    // `isExpired` boolean gets frozen while the tab is hidden — no dispatches,
    // no re-renders, so the ref stays stale for hours.
    const expiresAtRef = useRef(expiresAt)

    useEffect(() => { apiRef.current = api }, [api])
    useEffect(() => { orgRef.current = currentOrganization }, [currentOrganization])
    useEffect(() => { dispatchRef.current = dispatch }, [dispatch])
    useEffect(() => { tokenRef.current = token }, [token])
    useEffect(() => { expiresAtRef.current = expiresAt }, [expiresAt])

    // --- BroadcastChannel setup ---
    useEffect(() => {
        if (typeof window === 'undefined') return

        const channel = new BroadcastChannel(TOKEN_BROADCAST_CHANNEL)
        channelRef.current = channel

        const handleMessage = (event: MessageEvent<BroadcastMessage>) => {
            const { type, token: newToken, expiresIn } = event.data
            if (type === 'TOKEN_UPDATE' && newToken) {
                dispatchRef.current(setToken({ token: newToken, expiresIn: expiresIn || 600 }))
            } else if (type === 'TOKEN_CLEAR') {
                dispatchRef.current(clearToken())
            }
        }

        channel.addEventListener('message', handleMessage)
        return () => {
            channel.removeEventListener('message', handleMessage)
            channel.close()
            channelRef.current = null
        }
    }, [])

    // --- Stable helper functions (no deps, use refs internally) ---
    const broadcastToken = useCallback((newToken: string, expiresIn: number) => {
        channelRef.current?.postMessage({
            type: 'TOKEN_UPDATE',
            token: newToken,
            expiresIn
        } as BroadcastMessage)
    }, [])

    const broadcastClear = useCallback(() => {
        channelRef.current?.postMessage({ type: 'TOKEN_CLEAR' } as BroadcastMessage)
    }, [])

    const clearRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current)
            refreshTimerRef.current = null
        }
    }, [])

    const fetchFromSession = useCallback(async (): Promise<AuthTokenResponse | null> => {
        const org = orgRef.current
        if (!org) return null
        try {
            const response = await apiRef.current.request<AuthTokenResponse>(
                `/jwt/${org}/from-session`,
                {
                    method: 'post',
                    data: getDeviceInfo(),
                    config: { service: ServiceModules.AUTH }
                }
            )
            return response.data
        } catch {
            return null
        }
    }, [])

    const refreshViaEndpoint = useCallback(async (): Promise<AuthTokenResponse | null> => {
        const org = orgRef.current
        if (!org) return null
        try {
            const response = await apiRef.current.request<AuthTokenResponse>(
                `/jwt/${org}/refresh`,
                {
                    method: 'post',
                    config: { service: ServiceModules.AUTH }
                }
            )
            return response.data
        } catch {
            return null
        }
    }, [])

    const handleTokenResponse = useCallback((data: AuthTokenResponse): number => {
        const expiresIn = data.expires_in || 600
        dispatchRef.current(setToken({ token: data.access_token, expiresIn }))
        broadcastToken(data.access_token, expiresIn)
        isErrorRef.current = false
        return expiresIn
    }, [broadcastToken])

    const performRefresh = useCallback(async (): Promise<number | null> => {
        if (isRefreshingRef.current) return null
        isRefreshingRef.current = true
        isLoadingRef.current = true

        try {
            // If we already have a token, try the lightweight refresh endpoint first
            if (hasInitialTokenRef.current) {
                const refreshResult = await refreshViaEndpoint()
                if (refreshResult?.access_token) {
                    return handleTokenResponse(refreshResult)
                }
            }

            // Fallback (or first-time): use from-session
            const sessionResult = await fetchFromSession()
            if (sessionResult?.access_token) {
                hasInitialTokenRef.current = true
                return handleTokenResponse(sessionResult)
            }

            // Both failed — clear token
            dispatchRef.current(clearToken())
            broadcastClear()
            isErrorRef.current = true
            return null
        } finally {
            isRefreshingRef.current = false
            isLoadingRef.current = false
        }
    }, [refreshViaEndpoint, fetchFromSession, handleTokenResponse, broadcastClear])

    // Store scheduleRefresh in a ref so it can self-reference in setTimeout
    const scheduleRefreshRef = useRef<(() => Promise<void>) | undefined>(undefined)

    useEffect(() => {
        scheduleRefreshRef.current = async () => {
            clearRefreshTimer()

            // Only refresh in the visible tab
            if (typeof document !== 'undefined' && document.hidden) return

            const expiresIn = await performRefresh()
            if (expiresIn === null) return

            // Schedule next refresh: (expires_in - 60s) before expiry, minimum 30s
            const nextRefreshMs = Math.max((expiresIn - 60) * 1000, 30_000)
            refreshTimerRef.current = setTimeout(() => {
                scheduleRefreshRef.current?.()
            }, nextRefreshMs)
        }
    }, [clearRefreshTimer, performRefresh])

    // --- Initial fetch on mount / org change ---
    useEffect(() => {
        if (!currentOrganization) return

        hasInitialTokenRef.current = false
        scheduleRefreshRef.current?.()

        return () => {
            clearRefreshTimer()
        }
    }, [currentOrganization, clearRefreshTimer])

    // --- Visibility / focus: refresh on tab return if token is stale ---
    useEffect(() => {
        if (typeof document === 'undefined') return

        const handleTabActive = () => {
            if (document.hidden) {
                clearRefreshTimer()
                return
            }

            const now = Date.now()
            const expAt = expiresAtRef.current
            const isCurrentlyExpired =
                !expAt || now >= expAt - TOKEN_EXPIRY_BUFFER_MS

            if (isCurrentlyExpired || !tokenRef.current) {
                scheduleRefreshRef.current?.()
                return
            }

            const remainingMs = expAt - TOKEN_EXPIRY_BUFFER_MS - now
            clearRefreshTimer()
            refreshTimerRef.current = setTimeout(() => {
                scheduleRefreshRef.current?.()
            }, Math.max(remainingMs, 0))
        }

        document.addEventListener('visibilitychange', handleTabActive)
        window.addEventListener('focus', handleTabActive)
        return () => {
            document.removeEventListener('visibilitychange', handleTabActive)
            window.removeEventListener('focus', handleTabActive)
        }
    }, [clearRefreshTimer])

    // --- Public API ---
    const refreshTokenFn = useCallback(async (): Promise<void> => {
        await performRefresh()
    }, [performRefresh])

    const clearAuthToken = useCallback((): void => {
        clearRefreshTimer()
        dispatch(clearToken())
        broadcastClear()
    }, [dispatch, broadcastClear, clearRefreshTimer])

    return {
        token,
        isLoading: isLoadingRef.current,
        isError: isErrorRef.current,
        refreshToken: refreshTokenFn,
        clearAuthToken
    }
}

export function useGetAccessToken(): string | null {
    const { token } = useAppSelector((state) => state.authToken)
    return token
}

export default useAuthToken
